import React, { useEffect } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useSocialMediaSystem, SocialNetwork } from '../../hooks/useSocialMediaSystem';
import { 
  Instagram, 
  Facebook, 
  Linkedin, 
  Music, 
  Camera, 
  PinIcon, 
  ExternalLink,
  MessageCircle,
  Send,
  X
} from 'lucide-react';
import { useThemeSettings } from '../../hooks/useThemeSettings';

// Social media icons mapping
const SOCIAL_ICONS = {
  instagram: Instagram,
  tiktok: Camera,
  linkedin: Linkedin,
  facebook: Facebook,
  snapchat: Camera,
  pinterest: PinIcon,
  spotify: Music,
  whatsapp: MessageCircle,
  discord: MessageCircle,
  messenger: Send,
  twitter: X
};

// Social media colors mapping
const SOCIAL_COLORS = {
  instagram: 'bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400',
  tiktok: 'bg-black',
  linkedin: 'bg-blue-600',
  facebook: 'bg-blue-500',
  snapchat: 'bg-yellow-400',
  pinterest: 'bg-red-600',
  spotify: 'bg-green-600',
  whatsapp: 'bg-green-500',
  discord: 'bg-indigo-600',
  messenger: 'bg-blue-400',
  twitter: 'bg-black'
};

// Deep link patterns for mobile apps
const DEEP_LINKS = {
  instagram: {
    profile: 'instagram://user?username=',
    fallback: 'https://instagram.com/'
  },
  facebook: {
    profile: 'fb://profile/',
    page: 'fb://page/',
    fallback: 'https://facebook.com/'
  },
  twitter: {
    profile: 'twitter://user?screen_name=',
    fallback: 'https://twitter.com/'
  },
  linkedin: {
    profile: 'linkedin://profile/',
    fallback: 'https://linkedin.com/in/'
  },
  tiktok: {
    profile: 'tiktok://user/',
    fallback: 'https://tiktok.com/@'
  },
  whatsapp: {
    chat: 'whatsapp://send?phone=',
    fallback: 'https://wa.me/'
  },
  snapchat: {
    profile: 'snapchat://add/',
    fallback: 'https://snapchat.com/add/'
  },
  pinterest: {
    profile: 'pinterest://user/',
    fallback: 'https://pinterest.com/'
  },
  spotify: {
    profile: 'spotify:user:',
    fallback: 'https://open.spotify.com/user/'
  },
  discord: {
    server: 'discord://invite/',
    fallback: 'https://discord.gg/'
  },
  messenger: {
    profile: 'fb-messenger://user/',
    fallback: 'https://m.me/'
  }
};

export function SocialMediaButtons() {
  const { chatbotPublicId } = useChatbot();
  const { socialNetworksByChatbot, loading, error, initialized, initialize } = useSocialMediaSystem();
  const { theme } = useThemeSettings();

  const socialNetworks = chatbotPublicId ? socialNetworksByChatbot[chatbotPublicId] || [] : [];

  useEffect(() => {
    // Initialiser seulement si nécessaire
    if (chatbotPublicId && !initialized[chatbotPublicId]) {
      initialize(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initialize]);

  // Utiliser useMemo pour éviter des recalculs inutiles à chaque rendu
  const enabledNetworks = React.useMemo(() => {
    return socialNetworks
      .filter((network: SocialNetwork) => network?.enabled)
      .sort((a: SocialNetwork, b: SocialNetwork) => (a?.position || 0) - (b?.position || 0));
  }, [socialNetworks]);

  if (loading && !initialized[chatbotPublicId || '']) {
    return (
      <div className="flex justify-center items-center h-16">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  if (!enabledNetworks || enabledNetworks.length === 0) {
    return null;
  }

  // Function to check if we're on a mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Function to extract username from URL
  const extractUsername = (url: string, type: string): string => {
    try {
      const urlObj = new URL(url);
      
      // Remove protocol and www if present
      let fullPath = urlObj.hostname.replace('www.', '') + urlObj.pathname;
      
      // Remove trailing slash if present
      if (fullPath.endsWith('/')) {
        fullPath = fullPath.slice(0, -1);
      }
      
      switch (type) {
        case 'instagram':
          // instagram.com/username
          return fullPath.split('instagram.com/')[1] || '';
        
        case 'facebook':
          // facebook.com/username or facebook.com/pages/name/id
          const fbPath = fullPath.split('facebook.com/')[1] || '';
          return fbPath.includes('pages/') ? fbPath.split('/').pop() || '' : fbPath;
        
        case 'twitter':
          // twitter.com/username
          return fullPath.split('twitter.com/')[1] || '';
        
        case 'linkedin':
          // linkedin.com/in/username
          return fullPath.includes('/in/') ? fullPath.split('/in/')[1] : '';
        
        case 'tiktok':
          // tiktok.com/@username
          const tiktokPath = fullPath.split('tiktok.com/')[1] || '';
          return tiktokPath.startsWith('@') ? tiktokPath.substring(1) : tiktokPath;
        
        case 'whatsapp':
          // wa.me/number
          return fullPath.split('wa.me/')[1] || '';
        
        case 'snapchat':
          // snapchat.com/add/username
          return fullPath.split('snapchat.com/add/')[1] || '';
        
        case 'pinterest':
          // pinterest.com/username
          return fullPath.split('pinterest.com/')[1] || '';
        
        case 'spotify':
          // open.spotify.com/user/username
          return fullPath.includes('/user/') ? fullPath.split('/user/')[1] : '';
        
        case 'discord':
          // discord.gg/invite
          return fullPath.split('discord.gg/')[1] || '';
        
        case 'messenger':
          // m.me/username
          return fullPath.split('m.me/')[1] || '';
        
        default:
          return '';
      }
    } catch (err) {
      console.warn(`Error extracting username from URL for ${type}:`, err);
      return '';
    }
  };

  const handleOpenSocialMedia = (url: string, type: string) => {
    if (!url) return;
    
    // Ensure URL has protocol
    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    
    // On mobile, try to open the native app if available
    if (isMobileDevice()) {
      const deepLinkInfo = DEEP_LINKS[type as keyof typeof DEEP_LINKS];
      
      if (deepLinkInfo) {
        const username = extractUsername(formattedUrl, type);
        
        if (username) {
          // Create the deep link
          let deepLink = '';
          
          if (type === 'facebook') {
            // Facebook needs special handling for pages vs profiles
            if (formattedUrl.includes('facebook.com/pages/')) {
              deepLink = (deepLinkInfo as { page: string }).page + username;
            } else {
              deepLink = (deepLinkInfo as { profile: string }).profile + username;
            }
          } else if (type === 'whatsapp') {
            // WhatsApp needs special handling for phone numbers
            deepLink = (deepLinkInfo as { chat: string }).chat + username;
          } else {
            // Default handling for other platforms
            deepLink = (deepLinkInfo as { profile: string }).profile + username;
          }
          
          // Try to open the app with the deep link
          const fallbackUrl = deepLinkInfo.fallback + username;
          
          // Create an invisible iframe to try opening the app
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          document.body.appendChild(iframe);
          
          // Set a timeout to open the fallback URL if the app doesn't open
          setTimeout(() => {
            window.location.href = fallbackUrl;
            document.body.removeChild(iframe);
          }, 2000);
          
          // Try to open the app
          iframe.src = deepLink;
          
          return;
        }
      }
    }
    
    // Fallback to opening in a new tab
    window.open(formattedUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-wrap gap-2">
      {enabledNetworks.map((network) => {
        const IconComponent = SOCIAL_ICONS[network.type as keyof typeof SOCIAL_ICONS] || ExternalLink;
        const bgColorClass = SOCIAL_COLORS[network.type as keyof typeof SOCIAL_COLORS] || 'bg-gray-600';
        
        // If no name is provided, show only the icon
        if (!network.name || network.name.trim() === '') {
          return (
            <button
              key={network.id}
              onClick={() => handleOpenSocialMedia(network.url, network.type)}
              className={`w-6 h-6 rounded-full ${bgColorClass} flex items-center justify-center transition-transform hover:scale-110`}
              title={network.type.charAt(0).toUpperCase() + network.type.slice(1)}
            >
              <IconComponent className="w-3 h-3 text-white" />
            </button>
          );
        }
        
        // Otherwise show icon with text
        return (
          <button
            key={network.id}
            onClick={() => handleOpenSocialMedia(network.url, network.type)}
            className={`p-1.5 rounded-lg transition-colors shadow-sm flex items-center gap-1.5 ${
              theme.mode === 'dark' 
                ? 'bg-white/5 hover:bg-white/10 text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            }`}
            title={network.name}
          >
            <span className={`w-4 h-4 rounded-full ${bgColorClass} flex items-center justify-center`}>
              <IconComponent className="w-2.5 h-2.5 text-white" />
            </span>
            <span className="text-xs">{network.name}</span>
          </button>
        );
      })}
    </div>
  );
}